import { z } from "zod/v4";

import { ExperienceCategoryList } from "../../../dtos/experience-categories";
import { ExperienceTypeList } from "../../../dtos/experiences";
import {
  PluginCurrencySchema,
  PluginOnsiteInjectionSchema,
  PluginRedirectSchema_2024_05_05,
  PluginUserInterfaceSchema_2024_05_05,
} from "../config";
import { PluginAudienceSchema_2024_05_05 } from "../config/audiences/2024-05-05";
import { PluginExperienceProductsSchema_2024_05_05 } from "../config/experience-products/2024-05-05";
import { PluginExperiencePageTargetingSchema_2024_05_05 } from "../config/experiencePageTargeting/2024-05-05";
import { PluginConfigVersionList } from "../versions";

const PluginInitVariationSchema = z.object({
  id: z.uuid(),
  experienceId: z.uuid(),
  name: z.string(),
  percentage: z.number(),
  isControl: z.boolean().default(false),
  customCss: z.string().nullish(),
  customJs: z.string().nullish(),
});
export type PluginInitVariationType = z.infer<typeof PluginInitVariationSchema>;

const PluginInitAudienceSchema = PluginAudienceSchema_2024_05_05;

const PluginInitPageTargetingSchema =
  PluginExperiencePageTargetingSchema_2024_05_05;

const PluginInitExperienceProductSchema =
  PluginExperienceProductsSchema_2024_05_05;

const PluginInitExperienceSchema_2024_05_05 = z.object({
  id: z.uuid(),
  isPreview: z.boolean().optional().default(true),
  type: z.enum(ExperienceTypeList),
  category: z.enum(ExperienceCategoryList),
  testTypes: z.object({
    hasTestPricing: z.boolean().default(true),
    hasTestCampaign: z.boolean().default(false),
  }),
  measurementId: z.string().optional(),
  findReplaceSelectors: z.string().array().nullish(),
  requiresLink: z.boolean(),
});
export type PluginInitExperienceType_2024_05_05 = z.infer<
  typeof PluginInitExperienceSchema_2024_05_05
>;

const PluginInitOptionsSchema = z.object({
  domain: z.string().nullish(),
  isHeadlessStorefront: z.boolean().nullish(),
  shouldRedirect: z.boolean().nullish(),
  shouldDuplicateProducts: z.boolean().nullish(),
  locale: z.string().nullish(),
  metricsSampleRate: z.number().nullish(),
  useBrowserLocale: z.boolean().nullish(),
  currencyFn: z.string().nullish(),
  priceSlittingEnabled: z.boolean().nullish(),
  currencyFormat: PluginCurrencySchema.nullish(),
  eagerUpdateDom: z.boolean().nullish(),
  shopifyFunctionsEnabled: z.boolean().nullish(),
  shopifyFunctionsForPricingEnabled: z.boolean().nullish(),
  lazyCartUpdate: z.boolean().nullish(),
  useBeacon: z.boolean().nullish(),
  javascript: z.string().nullish(),
  addIgIdToCartAttrs: z.boolean().nullish(),
});

const PluginInitExclusionGroupEntitySchema = z.object({
  id: z.uuid(),
  experienceId: z.uuid(), //.optional(),
  order: z.number(),
  hasPriorityOrder: z.boolean(),
  percentage: z.number(),
});

const PluginInitOfferSchema = z.object({
  id: z.uuid(),
  enabled: z.boolean(),
});

const PluginInitExclusionGroupSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  reassignOnEnd: z.boolean(),
  hasWeightedValues: z.boolean(),
  exclusionGroupEntities: PluginInitExclusionGroupEntitySchema.array().default(
    []
  ),
});

const PluginInitExperienceProductsSchema =
  PluginExperienceProductsSchema_2024_05_05.pick({
    id: true,
    altHandles: true,
    handle: true,
    experienceId: true,
  });

export type PluginInitExperienceProductsType = z.infer<
  typeof PluginInitExperienceProductsSchema
>;

export const PluginInitConfigSchema_2024_05_05 = z.object({
  storeName: z.string(),
  orgId: z.uuid(),
  buildId: z.number(),
  version: z.enum(PluginConfigVersionList),
  priceSelectors: z.string().array().nullish(),
  stayAiSelectors: z.string().array().nullish(),
  experiences: PluginInitExperienceSchema_2024_05_05.array().default([]),
  onsiteInjections: PluginOnsiteInjectionSchema.array().default([]),
  userInterfaces: PluginUserInterfaceSchema_2024_05_05.array().default([]),
  offers: PluginInitOfferSchema.array().default([]),
  variations: PluginInitVariationSchema.array().default([]),
  audiences: PluginInitAudienceSchema.array().default([]),
  experiencePageTargeting: PluginInitPageTargetingSchema.array().default([]),
  redirects: PluginRedirectSchema_2024_05_05.array().default([]),
  options: PluginInitOptionsSchema,
  exclusionGroups: PluginInitExclusionGroupSchema.array().default([]),
  experienceProducts: PluginInitExperienceProductsSchema.array().default([]),
});

export type PluginInitConfigType_2024_05_05 = z.infer<
  typeof PluginInitConfigSchema_2024_05_05
>;
